// Respaldo legacy v1 de respuestas de Formularios (`answers.json` + idb-keyval). Sigue vivo cuando la base
// local (SQLite) no abre (ISS-1009) o una escritura v2 falla, y es el único drenado del video. Lo que
// escribe y lee este archivo NO pasa por la capa v2 (data + outbox).
//
// Reglas del módulo: no importa la store, Quasar, boot/i18n, el barrel src/utils ni services de negocio. La UI,
// el envío HTTP y el estado compartido se quedan en las actions de la store (stores/occurrences/actions.js), que delegan aquí
// (la mayoría con el mismo nombre; `saveOfflineAnswers` → `drainLegacyAnswers`).

// Librerias
import { Capacitor } from '@capacitor/core'
import { Filesystem } from '@capacitor/filesystem'
import { set, del, get } from 'idb-keyval'

// Services
import { OfflineStorage } from '@/services/offline-storage'
import { withAnswersLock } from '@/services/offline-answers-lock'
import {
  readImageFromFileSystem,
  readImageAsBlob,
  deleteImageFromFileSystem,
  saveImageToFileSystem
} from '@/services/offline-images'

// Utils
import { isLegacyAnswerRow, legacyRowKey, sameLegacyRow } from '@/utils/legacyAnswers'

// Offline
import { dbgWarn as offlineDbgWarn } from '@/offline/logger'
import { recordBreadcrumb } from '@/offline/telemetry'
import { serializeExtraProperties } from '@/offline/sync/builders/extra-properties'
import { createKeyedSerializer } from '@/offline/serializeWrites'

// Una sola cadena por ocurrencia|ítem|tipo para escribir y borrar addons del respaldo (persistLegacyAddon y
// clearLegacyAddon la comparten: tiene que ser una sola instancia).
const legacyAddonWriteSerializer = createKeyedSerializer()

// Video o documento con su archivo en IndexedDB (idb-keyval) bajo `answer_ref`.
const isStoredFileAddon = row => row.type === 'video' || (row.type === 'document' && !!row.answer_ref)

export async function waitForIndexedDBReady (timeout = 4000) {
  const start = Date.now()
  while (Date.now() - start < timeout) {
    try {
      const testKey = 'test_key'
      await set(testKey, 'ok')
      await del(testKey)
      return true
    } catch (err) {
      console.error(`Esperando que IndexedDB esté listo... ${err}`)
      await new Promise(resolve => setTimeout(resolve, 300))
    }
  }
  console.warn('IndexedDB no estuvo listo a tiempo')
  return false
}

export function writeLegacyAnswer (answer, { allowPhotoReplace = false, onlyExtraProperties = false } = {}) {
  return withAnswersLock(async () => {
    const isImage = answer.type === 'picture' || answer.answer_ref || answer.image_ref

    const offlineAnswers = await OfflineStorage.getAnswers() || []

    const existingIndex = offlineAnswers.findIndex(_a =>
      isLegacyAnswerRow(_a, answer.occurrence_id, answer.item_id)
    )
    const existing = existingIndex > -1 ? offlineAnswers[existingIndex] : null
    if (existing?.created_at) answer.created_at = existing.created_at
    const isPhotoItem = !answer.type && typeof answer.value === 'string' && answer.value.startsWith('file:///')
    const isPhotoData = !answer.type && !isImage && typeof answer.value === 'string' && answer.value.startsWith('data:image/')
    const replacesPhoto = isPhotoItem || isPhotoData || (isImage && typeof answer.value === 'string' && answer.value.startsWith('data:image/'))

    if (existing?.localPath && !answer.localPath && !allowPhotoReplace) {
      if (onlyExtraProperties) {
        offlineAnswers[existingIndex] = {
          ...existing,
          extra_properties: answer.extra_properties || [],
          current_points: answer.current_points,
          total_points: answer.total_points,
          updated_at: answer.updated_at
        }
        await OfflineStorage.setAnswers(offlineAnswers)
        return
      }
      if (answer.value !== '' && !replacesPhoto) {
        recordBreadcrumb('form.capture', `guardado sin foto descartado: foto pendiente (item ${answer.item_id})`, 'warning', {
          item_id: answer.item_id,
          occurrence_id: answer.occurrence_id
        })
        offlineDbgWarn('photo', `item ${answer.item_id}: se conserva la foto pendiente del respaldo y se descarta un guardado sin foto`)
        return
      }
    }

    if (existingIndex > -1) offlineAnswers.splice(existingIndex, 1)

    const releaseReplacedPhoto = async () => {
      const replacedPath = existing?.localPath
      if (!replacedPath || replacedPath === answer.value || offlineAnswers.some(a => a.localPath === replacedPath)) return
      await deleteImageFromFileSystem(replacedPath)
    }

    if (isPhotoItem || (isPhotoData && Capacitor.isNativePlatform())) {
      const imageRef = `img-${Date.now()}-${answer.item_id}`

      const base64 = isPhotoData ? answer.value : await readImageFromFileSystem(answer.value)
      if (!base64) throw new Error('No se pudo leer imagen temporal')

      const localPath = await saveImageToFileSystem(imageRef, base64)

      offlineAnswers.push({
        occurrence_id: answer.occurrence_id,
        form_assignment_location_occurrence_id: answer.form_assignment_location_occurrence_id,
        item_id: answer.item_id,
        image_ref: imageRef,
        localPath,
        value: null,
        current_points: answer.current_points,
        total_points: answer.total_points,
        extra_properties: answer.extra_properties || [],
        created_at: answer.created_at,
        updated_at: answer.updated_at
      })
      await OfflineStorage.setAnswers(offlineAnswers)
      await releaseReplacedPhoto()
      return
    }

    if (isImage) {
      if (typeof answer.value === 'string' && answer.value.startsWith('data:image/')) {
        const fileName = `img-${Date.now()}-${answer.item_id}`
        const localPath = await saveImageToFileSystem(fileName, answer.value)
        answer.answer_ref = fileName
        answer.localPath = localPath
        delete answer.value
      }

      offlineAnswers.push(answer)
      await OfflineStorage.setAnswers(offlineAnswers)
      await releaseReplacedPhoto()
      return
    }

    let stableValue = answer.value
    if (typeof answer.value === 'string' && answer.value.startsWith('blob:')) {
      try {
        const res = await fetch(answer.value)
        if (res.ok) {
          const blobData = await res.blob()
          stableValue = await new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result)
            reader.onerror = reject
            reader.readAsDataURL(blobData)
          })
        }
      } catch (e) {
        offlineDbgWarn('photo', 'blob URL → base64 fallida, se guarda como está', e)
      }
    }

    offlineAnswers.push({
      occurrence_id: answer.occurrence_id,
      form_assignment_location_occurrence_id: answer.form_assignment_location_occurrence_id,
      item_id: answer.item_id,
      value: stableValue,
      extra_properties: answer.extra_properties || [],
      current_points: answer.current_points,
      total_points: answer.total_points,
      created_at: answer.created_at,
      updated_at: answer.updated_at
    })
    await OfflineStorage.setAnswers(offlineAnswers)
    await releaseReplacedPhoto()
  })
}

export async function listRealPendingLegacyAnswers (occurrenceId) {
  const offlineAnswers = await OfflineStorage.getAnswers()
  return filterRealPending(offlineAnswers.filter(a => Number(a.occurrence_id) === Number(occurrenceId)))
}

// Revisa y quita en una sola toma del candado: sin pendientes reales, quita las filas de la ocurrencia que
// se revisaron. Devuelve las pendientes reales.
export function discardStaleLegacyAnswers (occurrenceId) {
  return withAnswersLock(async () => {
    const offlineAnswers = await OfflineStorage.getAnswers()
    const related = offlineAnswers.filter(a => Number(a.occurrence_id) === Number(occurrenceId))
    const realPending = await filterRealPending(related)
    if (realPending.length === 0 && related.length > 0) {
      await OfflineStorage.setAnswers(offlineAnswers.filter(a => !related.includes(a)))
    }
    return realPending
  })
}

async function filterRealPending (related) {
  const videoBlobChecks = await Promise.all(
    related.map(async a => {
      if (a.type !== 'video') return true
      if (!a.answer_ref) return false
      try {
        const blob = await get(a.answer_ref)
        return blob instanceof Blob
      } catch {
        return false
      }
    })
  )

  return related.filter((a, i) => {
    if (!a.type) return true

    if (a.type.includes('picture')) {
      return (
        !!a.localPath ||
        (typeof a.value === 'string' && a.value.startsWith('data:image')) ||
        !!a.answer_ref
      )
    }

    if (a.type === 'video') {
      return videoBlobChecks[i]
    }

    return true
  })
}

async function fileUriToBlob (path) {
  if (path.startsWith('content://')) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('GET', path)
      xhr.responseType = 'blob'
      xhr.onload = () => resolve(xhr.response)
      xhr.onerror = reject
      xhr.send()
    })
  }

  const { data } = await Filesystem.readFile({ path })
  const binary = atob(data)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.codePointAt(i)
  return new Blob([bytes], { type: 'image/jpeg' })
}

// Drena a la API el respaldo v1 de una ocurrencia, en lotes de 2, y purga solo lo que el servidor
// confirmó. Devuelve { ok, pending, failed }: filas que siguen en el respaldo y lotes fallidos.
// La UI queda fuera: se avisa por callbacks (`onBadNetwork`, `onSyncStart`, `onPartial`, `onSyncEnd`).
// El envío HTTP también queda fuera: `sendBatch(formData)` lo pone quien llama (este módulo no importa services de negocio).
// `getOccurrenceId` es función porque el id se lee después de esperar a IndexedDB, no al entrar.
// `onSyncEnd` solo se llama en la salida normal; si algo lanza, quien llama oculta el loading.
// Con `skipBadNetworkCheck` se intenta la subida aunque la señal se vea baja.
// Una corrida a la vez por ocurrencia; las llamadas que llegan mientras corre comparten una corrida más al terminar.
export async function drainLegacyAnswers (options) {
  await waitForIndexedDBReady()
  const occId = options.getOccurrenceId()
  return runSingleLegacyDrain(occId, () => drainOccurrenceAnswers(occId, options))
}

const legacyDrains = new Map()

function runSingleLegacyDrain (occId, drain) {
  const running = legacyDrains.get(occId)
  if (running) {
    if (!running.next) {
      running.next = running.current.catch(() => {}).then(() => {
        legacyDrains.delete(occId)
        return runSingleLegacyDrain(occId, drain)
      })
    }
    return running.next
  }

  const entry = { next: null }
  entry.current = drain().finally(() => {
    if (!entry.next) legacyDrains.delete(occId)
  })
  legacyDrains.set(occId, entry)
  return entry.current
}

async function drainOccurrenceAnswers (occId, { sendBatch, onBadNetwork, onSyncStart, onPartial, onSyncEnd, skipBadNetworkCheck = false }) {
  const net = navigator.connection || {}
  const badNetwork = !skipBadNetworkCheck && (
    (typeof net.downlink === 'number' && net.downlink < 0.5) ||
    (typeof net.effectiveType === 'string' && ['slow-2g', '2g', '3g'].includes(net.effectiveType)) ||
    (typeof net.rtt === 'number' && net.rtt > 800)
  )

  if (badNetwork) {
    onBadNetwork()
    // No se drenó nada: hay pendientes. El caller NO debe cantar éxito (ya se avisó arriba).
    return { ok: true, pending: 1, failed: 0 }
  }

  let answers = await OfflineStorage.getAnswers()
  answers = answers.filter(a => String(a.occurrence_id).trim() === occId)

  if (!answers?.length) return { ok: true, pending: 0, failed: 0 }

  onSyncStart()

  const CHUNK_SIZE = 2
  let failedBatches = 0

  // TKT-2227: solo se purga del almacén local lo que de VERDAD se envió Y se confirmó en un lote
  // exitoso, y solo la fila idéntica a la enviada: si cambió durante la subida (otro valor, otra
  // foto), la versión nueva se conserva para el siguiente intento.
  const syncedRows = new Set()

  for (let i = 0; i < answers.length; i += CHUNK_SIZE) {
    const batch = answers.slice(i, i + CHUNK_SIZE)
    const formData = new FormData()

    let answerIndex = 0
    let addonIndex = 0
    const answerFileMap = []
    // Respuestas/addons de ESTE lote que SÍ se adjuntaron al FormData (las omitidas por no
    // resolver su binario NO entran). Se promueven a syncedRows solo si el POST del lote responde.
    const batchIncluded = []

    for (const ans of batch) {
      const isAddon =
        ans.type &&
        (ans.type.includes('picture') || ans.type === 'comment' || ans.type === 'document' || ans.type === 'video') &&
        ans.type !== 'photo'

      if (isAddon) {
        const isPicture = ans.type.includes('picture')

        if (isPicture) {
          // Fix 2+3: resolver el blob ANTES de agregar al FormData.
          // Si no hay blob resuelto, omitir el addon por completo para no mandar
          // el answer_ref como placeholder al backend.
          let resolvedBlob = null
          const ref = ans.answer_ref || ans.image_ref

          if (ans.localPath && Capacitor.isNativePlatform()) {
            try {
              resolvedBlob = await fileUriToBlob(ans.localPath)
            } catch (e) {
              console.error('[SYNC] fileUriToBlob falló', e)
            }
          } else if (ref) {
            // Intento 1: IDB (datos legacy o guardados por flujos anteriores)
            try {
              const idbVal = await get(ref)
              if (idbVal instanceof Blob) {
                resolvedBlob = idbVal
              } else if (typeof idbVal === 'string' && idbVal.startsWith('data:image')) {
                const res = await fetch(idbVal)
                const b = await res.blob()
                if (b && b.size > 0) resolvedBlob = b
              }
            } catch (e) {
              console.error('[SYNC] error leyendo IDB', e)
            }

            // Intento 2 (Fix 2): blob URL todavía válido en la misma sesión
            if (!resolvedBlob && typeof ans.answer === 'string' && ans.answer.startsWith('blob:')) {
              try {
                const res = await fetch(ans.answer)
                if (res.ok) {
                  const b = await res.blob()
                  if (b && b.size > 0) resolvedBlob = b
                }
              } catch {
                // blob URL expiró (sesión distinta) — no hay imagen recuperable
              }
            }
          }

          if (!resolvedBlob) {
            // Fix 3: sin blob no se incluye en el FormData — evita que el backend
            // reciba el answer_ref como answer y sobreescriba datos válidos.
            console.warn('[SYNC] addon picture sin blob resuelto, se omite del batch:', ref)
            continue
          }

          formData.append(
            `addons_values[${addonIndex}][occurrence_id]`,
            ans.occurrence_id || ans.form_assignment_location_occurrence_id
          )
          formData.append(`addons_values[${addonIndex}][item_id]`, ans.item_id)
          formData.append(`addons_values[${addonIndex}][type]`, ans.type)
          formData.append(`addons_values[${addonIndex}][answer_ref]`, ref)
          formData.append(`addons_values[${addonIndex}][file]`, resolvedBlob, `${ref}.jpeg`)
          addonIndex++
          batchIncluded.push(ans)
        } else if (isStoredFileAddon(ans)) {
          const ref = ans.answer_ref
          if (ref) {
            try {
              const blob = await get(ref)
              if (blob instanceof Blob) {
                const fileName = ans.type === 'video' ? blob.name || `${ref}.mp4` : ans.filename || ref
                formData.append(`addons_values[${addonIndex}][occurrence_id]`, ans.occurrence_id || ans.form_assignment_location_occurrence_id)
                formData.append(`addons_values[${addonIndex}][item_id]`, ans.item_id)
                formData.append(`addons_values[${addonIndex}][type]`, ans.type)
                formData.append(`addons_values[${addonIndex}][answer_ref]`, ref)
                formData.append(`addons_values[${addonIndex}][file]`, blob, fileName)
                addonIndex++
                batchIncluded.push(ans)
              } else {
                console.warn(`[SYNC] ${ans.type} sin blob en IDB, se omite del batch:`, ref)
              }
            } catch (e) {
              console.error(`[SYNC] error recuperando ${ans.type} de IDB`, e)
            }
          }
        } else {
          // Comentarios y documentos: sin archivo, agregar directamente
          formData.append(
            `addons_values[${addonIndex}][occurrence_id]`,
            ans.occurrence_id || ans.form_assignment_location_occurrence_id
          )
          formData.append(`addons_values[${addonIndex}][item_id]`, ans.item_id)
          formData.append(`addons_values[${addonIndex}][type]`, ans.type)
          formData.append(`addons_values[${addonIndex}][answer]`, ans.answer || '')
          addonIndex++
          batchIncluded.push(ans)
        }
      } else {
        const isNativePhoto = !ans.type && ans.image_ref && ans.localPath

        let nativePhotoBlob = null
        if (isNativePhoto && Capacitor.isNativePlatform()) {
          try {
            nativePhotoBlob = await fileUriToBlob(ans.localPath)
          } catch (e) {
            console.error('[PHOTO ITEM] no se pudo leer la foto, se omite del envío:', e)
            recordBreadcrumb('sync', `foto de ítem ilegible omitida del envío (item ${ans.item_id})`, 'warning', {
              item_id: ans.item_id,
              occurrence_id: ans.occurrence_id
            })
            continue
          }
        }

        formData.append(`answers[${answerIndex}][occurrence_id]`, ans.occurrence_id)
        formData.append(`answers[${answerIndex}][form_assignment_location_occurrence_id]`, ans.form_assignment_location_occurrence_id)
        formData.append(`answers[${answerIndex}][item_id]`, ans.item_id)

        // TKT-2424: los registros v1 traen created_at/updated_at (saveItemAnswer, moment.utc
        // 'YYYY-MM-DD HH:mm:ss'); el flujo offline-v2 ya los adjunta en su builder, el legacy no.
        // Sin esto, lo que se sube desde el respaldo legacy llega con el tiempo de ejecución colapsado.
        if (ans.created_at) {
          formData.append(`answers[${answerIndex}][created_at]`, String(ans.created_at))
        }
        if (ans.updated_at) {
          formData.append(`answers[${answerIndex}][updated_at]`, String(ans.updated_at))
        }

        // B.9d — web + base64 o blob URL en value: convertir a Blob inline.
        // base64 es el caso normal tras los fixes de storage.
        // blob: URL solo aparece en entradas guardadas antes del fix (misma sesión).
        // Si la conversión falla, webPhotoBlob queda null y el fallback envía
        // el base64 como value (comportamiento anterior — funciona, solo es más costoso).
        let webPhotoBlob = null
        let generatedImageRef = null
        if (
          !ans.type &&
          !ans.localPath &&
          !ans.image_ref &&
          typeof ans.value === 'string' &&
          (ans.value.startsWith('data:image') || ans.value.startsWith('blob:'))
        ) {
          try {
            const fetchRes = await fetch(ans.value)
            if (!fetchRes.ok) throw new Error(`fetch base64 falló (${fetchRes.status})`)
            const b = await fetchRes.blob()
            if (!b || b.size === 0) throw new Error('Blob vacío al convertir base64')
            webPhotoBlob = b
            generatedImageRef = `img-${Date.now()}-${ans.item_id}`
            formData.append(`answers[${answerIndex}][image_ref]`, generatedImageRef)
          } catch (e) {
            console.warn('[B.9d] conversión base64→Blob fallida, se enviará como valor:', e)
          }
        }

        const isPhotoItem = isNativePhoto || webPhotoBlob !== null

        const rawValue = isNativePhoto
          ? ans.image_ref
          : (generatedImageRef || ans.value)

        let valueToSend = rawValue ?? ''

        if (rawValue !== null && typeof rawValue === 'object') {
          valueToSend = JSON.stringify(rawValue)
        }

        formData.append(
          `answers[${answerIndex}][value]`,
          valueToSend
        )

        formData.append(`answers[${answerIndex}][current_points]`, ans.current_points ?? 0)
        formData.append(`answers[${answerIndex}][total_points]`, ans.total_points ?? 0)

        const extraProps = serializeExtraProperties(ans.extra_properties)
        if (extraProps !== null) {
          formData.append(`answers[${answerIndex}][extra_properties]`, extraProps)
        }

        if (isNativePhoto && ans.image_ref) {
          formData.append(`answers[${answerIndex}][image_ref]`, ans.image_ref)
        }

        if (isPhotoItem) {
          answerFileMap.push({ ans, index: answerIndex, webPhotoBlob, nativePhotoBlob, generatedImageRef })
        }

        answerIndex++
        batchIncluded.push(ans)
      }
    }

    formData.append('app_version', import.meta.env.APP_VERSION)

    for (const { ans, index, webPhotoBlob, nativePhotoBlob, generatedImageRef } of answerFileMap) {
      try {
        if (nativePhotoBlob instanceof Blob) {
          formData.append(`answers[${index}][file]`, nativePhotoBlob, `${ans.image_ref}.jpeg`)
          continue
        }

        // Web: el binario ya se resolvió arriba (base64/blob: -> Blob). Sin este append
        // se mandaba image_ref sin archivo y la foto nunca llegaba al backend.
        if (webPhotoBlob instanceof Blob) {
          const fallbackImageRef = generatedImageRef || ans.image_ref || `img-${ans.item_id}`
          formData.append(
            `answers[${index}][file]`,
            webPhotoBlob,
            `${fallbackImageRef}.jpeg`
          )
        }
      } catch (e) {
        console.error('[PHOTO ITEM] error adjuntando archivo:', e)
      }
    }

    // Sin withTimeout aqui a proposito: el servicio ya limita el request con el timeout de
    // axios (UPLOAD_TIMEOUT_MS, 180s). Un tope de 20s corta cualquier batch con video grande
    // en red movil, que es justo el caso que este flujo protege.
    let syncResponse = null

    try {
      syncResponse = await sendBatch(formData)
    } catch (e) {
      console.error('[saveOfflineAnswers] batch failed', e)
      failedBatches++
      await new Promise(resolve => setTimeout(resolve, 300))
      continue
    }

    // El endpoint NO es atómico: acepta el batch (200) y aun así puede fallar al persistir una pieza.
    // Por eso las piezas con BINARIO se confirman POR-ÍTEM contra syncResponse.addons (el server las
    // devuelve con su value S3 real); un HTTP sin error NO basta. Esto cubre:
    //   - foto-addon (web/nativa) y video -> key item_id::type
    //   - FOTO-RESPUESTA (item-foto, sin type): el backend la confirma como addon type 'answer'
    //     (OfflineControllerCopy empuja processedAddons con type:'answer' al persistir el binario,
    //     tanto en la rama $uploadedFile como en la de base64) -> key item_id::answer.
    // Las piezas NATIVAS (localPath) —addon o respuesta— las gobierna el barrido de abajo, que además
    // BORRA el archivo local y también exige confirmación por-ítem. Texto/tabla/documento/comentario
    // se persisten atómicamente con el 200 (sin paso de storage aparte) -> el 200 del batch es su
    // única señal (y el contrato solo devuelve {success, addons}).
    if (syncResponse) {
      const confirmedAddonKeys = new Set(
        (Array.isArray(syncResponse.addons) ? syncResponse.addons : [])
          .filter(a => typeof a?.value === 'string' && a.value.length > 0)
          .map(a => `${a.item_id}::${a.type || 'answer'}`)
      )
      // Addon con archivo adjunto en esta función (arriesgan "200 pero binario no persistido").
      const isFileAddon = a =>
        (!!a.type && a.type.includes('picture') && a.type !== 'photo') || isStoredFileAddon(a)
      // Foto-RESPUESTA (item-foto): las filas que entraron a answerFileMap son justo las que llevan
      // binario de foto (nativa o web). Se confirman como addon type 'answer'.
      const photoAnswers = new Set(answerFileMap.map(x => x.ans))

      // Purga selectiva: qué de ESTE lote se da por sincronizado.
      for (const a of batchIncluded) {
        // Nativa (localPath): addon o respuesta -> la purga+borrado la hace el barrido de abajo,
        // que ya exige confirmación por-ítem del server. No la damos por sincronizada aquí.
        if (a.localPath && Capacitor.isNativePlatform()) continue

        if (isFileAddon(a) || photoAnswers.has(a)) {
          // foto-web addon / video / foto-respuesta web: solo si el server confirmó la pieza.
          if (confirmedAddonKeys.has(`${a.item_id}::${a.type || 'answer'}`)) syncedRows.add(a)
        } else {
          // texto / tabla / documento / comentario: su persistencia es atómica con el 200.
          syncedRows.add(a)
        }
      }

      // Pieza NATIVA (localPath): foto-addon o foto-respuesta. Purga del store + borra el archivo
      // local, SOLO si el backend la confirmó (valor S3 real). El endpoint no es atómico: puede fallar
      // una foto y responder 200 igual. Se itera batchIncluded (NO batch): una foto omitida por
      // binario no resuelto no entró al envío -> no se purga aunque la key item::type esté confirmada
      // por otra foto del mismo ítem. Se borra solo la fila idéntica a la enviada, y su archivo solo si
      // ninguna fila que se queda lo sigue usando.
      for (const ans of batchIncluded) {
        if (!ans.localPath || !Capacitor.isNativePlatform()) continue
        if (!confirmedAddonKeys.has(`${ans.item_id}::${ans.type || 'answer'}`)) continue

        try {
          const fileStillUsed = await withAnswersLock(async () => {
            const currentAnswers = await OfflineStorage.getAnswers() || []
            const remaining = currentAnswers.filter(a => !sameLegacyRow(a, ans))
            await OfflineStorage.setAnswers(remaining)
            return remaining.some(a => a.localPath === ans.localPath)
          })
          if (!fileStillUsed) await deleteImageFromFileSystem(ans.localPath)
        } catch (e) {
          console.error('[SYNC] error limpiando foto confirmada:', e)
        }
      }
    }

    await new Promise(resolve => setTimeout(resolve, 300))
  }

  // TKT-2227: purga SELECTIVA. Antes se borraba TODO el almacén de la occurrence en cuanto ningún
  // lote lanzaba error HTTP — pero un lote puede responder 200 habiendo OMITIDO una foto cuyo binario
  // no se pudo resolver (blob: expirado o IDB desalojado tras una sesión larga de horas). Eso borraba
  // localmente fotos que NUNCA llegaron al server, y con la base local ya vacía el usuario perdía la
  // auditoría. Ahora solo se quita lo que de verdad se confirmó en syncedRows; lo omitido y lo de un
  // lote fallido queda en OfflineStorage para el siguiente intento. (Las fotos nativas localPath ya
  // las limpió arriba el bloque de confirmación por respuesta del server.)
  const syncedKeys = new Set([...syncedRows].map(row => legacyRowKey(row)))
  const remaining = await withAnswersLock(async () => {
    const all = (await OfflineStorage.getAnswers()) || []
    const kept = all.filter(a =>
      String(a.occurrence_id).trim() !== occId || !syncedKeys.has(legacyRowKey(a))
    )
    await OfflineStorage.setAnswers(kept)
    return kept
  })
  // Solo se sueltan los binarios de lo que SÍ se sincronizó (evita huérfanos sin perder pendientes):
  // el blob de video o documento en idb-keyval y el archivo local de una foto-respuesta nativa ya purgada.
  for (const a of answers) {
    if (!syncedRows.has(a)) continue
    if (isStoredFileAddon(a) && a.answer_ref) {
      if (remaining.some(r => r.answer_ref === a.answer_ref)) continue
      try { await del(a.answer_ref) } catch (e) { console.error(`[${a.type}] del blob falló`, e) }
    } else if (!a.type && a.localPath && Capacitor.isNativePlatform()) {
      if (remaining.some(r => r.localPath === a.localPath)) continue
      try { await deleteImageFromFileSystem(a.localPath) } catch (e) { console.error('[PHOTO] del archivo local falló', e) }
    }
  }
  // Parcialidad: si un lote falló o quedaron piezas sin confirmar, NO se sincronizó todo. El caller no
  // debe cantar "sincronizadas correctamente" (era literalmente el toast del reporte original): se
  // avisa aquí y se devuelve el estado para que el caller ajuste su mensaje.
  const stillPending = remaining.filter(a => String(a.occurrence_id).trim() === occId).length
  if (failedBatches > 0 || stillPending > 0) {
    console.warn(`[SYNC] occ ${occId}: ${stillPending} fila(s) pendiente(s), ${failedBatches} lote(s) fallido(s); se reintentan al reabrir.`)
    onPartial()
  }

  onSyncEnd()
  return { ok: true, pending: stillPending, failed: failedBatches }
}

// Escribe un addon (foto, comentario, documento...) en el respaldo v1. Una sola cadena por ocurrencia, ítem y
// tipo (`legacyAddonWriteSerializer`, la misma que usa clearLegacyAddon). La store pone `processPhoto` (vive en
// el barrel src/utils, que este módulo no importa) y `onPersisted` (refleja el addon en su estado).
export function persistLegacyAddon (offlineAddon, { photoQualityProfile, processPhoto, onPersisted }) {
  const key = `${offlineAddon.occurrence_id}|${offlineAddon.item_id}|${offlineAddon.type}`
  return legacyAddonWriteSerializer(key, async () => {
    const answer = offlineAddon.answer
    const type = offlineAddon.type

    const isPicture = type === 'picture' || (typeof type === 'string' && type.includes('picture'))

    let photo = null
    let localPath = null
    if (isPicture && answer) {
      try {
        if (typeof answer === 'string' && (answer.startsWith('file://') || answer.startsWith('content://'))) {
          const blob = await readImageAsBlob(answer)
          if (blob) {
            photo = await processPhoto(blob, { profile: photoQualityProfile })
            localPath = answer
          }
        } else if (typeof answer === 'string' && answer.startsWith('data:image/')) {
          photo = await processPhoto(answer, { profile: photoQualityProfile })
        }
      } catch (err) {
        console.error('[persistLegacyOfflineAddon] Error procesando imagen:', err)
      }
    }

    try {
      await withAnswersLock(async () => {
        const offlineAnswers = await OfflineStorage.getAnswers()

        const existingIndex = offlineAnswers.findIndex(_a =>
          _a.occurrence_id === offlineAddon.occurrence_id &&
          _a.item_id === offlineAddon.item_id &&
          _a.type === offlineAddon.type
        )
        if (existingIndex > -1) offlineAnswers.splice(existingIndex, 1)

        if (localPath) offlineAddon.localPath = localPath
        if (photo) {
          offlineAddon.answer_ref = offlineAddon.answer_ref || `addon-${Date.now()}-${offlineAddon.item_id}-${offlineAddon.type}`
          offlineAddon.blob = photo.blob
          offlineAddon.answer = photo.url
          offlineAddon.thumbnail = photo.url
        }

        offlineAnswers.push(offlineAddon)
        await OfflineStorage.setAnswers(offlineAnswers)
      })
    } catch (err) {
      if (photo && offlineAddon.blob !== photo.blob) URL.revokeObjectURL(photo.url)
      throw err
    }

    if (answer) {
      onPersisted(offlineAddon)
    }
    return offlineAddon
  })
}

export async function clearLegacyAddon (offlineAddon) {
  const key = `${offlineAddon.occurrence_id}|${offlineAddon.item_id}|${offlineAddon.type}`
  await legacyAddonWriteSerializer(key, () => withAnswersLock(async () => {
    const offlineAnswers = await OfflineStorage.getAnswers()
    const filteredAnswers = offlineAnswers.filter(a =>
      !(a.occurrence_id === offlineAddon.occurrence_id &&
        a.item_id === offlineAddon.item_id &&
        a.type === offlineAddon.type)
    )
    await OfflineStorage.setAnswers(filteredAnswers)
  }))

  if (offlineAddon.localPath) {
    await deleteImageFromFileSystem(offlineAddon.localPath).catch(e => console.error(e))
  }
}

// Quita del respaldo la fila del addon de foto que el servidor ya confirmó (el try/catch queda en addImage).
export function removeSyncedLegacyAddonRow (syncedAddon) {
  return withAnswersLock(async () => {
    const offlineAnswers = await OfflineStorage.getAnswers() || []
    const filtered = offlineAnswers.filter(a =>
      !(
        Number(a.occurrence_id) === Number(syncedAddon.occurrence_id) &&
        Number(a.form_assignment_location_occurrence_id) === Number(syncedAddon.form_assignment_location_occurrence_id) &&
        Number(a.item_id) === Number(syncedAddon.item_id) &&
        a.type === syncedAddon.type &&
        (a.answer_ref || null) === (syncedAddon.answer_ref || null)
      )
    )
    await OfflineStorage.setAnswers(filtered)
  })
}

// Guarda el binario del video en IndexedDB (idb-keyval) bajo su referencia.
export function saveLegacyVideoBlob (ref, file) {
  return set(ref, file)
}

// Reemplaza en el respaldo la fila de video de la ocurrencia y el ítem por `row` (el try/catch queda en addVideo).
export function upsertLegacyVideoRow ({ occurrenceId, itemId, row }) {
  return withAnswersLock(async () => {
    const offlineAnswers = await OfflineStorage.getAnswers() || []
    const existing = offlineAnswers.findIndex(a =>
      Number(a.occurrence_id) === Number(occurrenceId) &&
      Number(a.item_id) === Number(itemId) &&
      a.type === 'video'
    )
    if (existing > -1) offlineAnswers.splice(existing, 1)
    offlineAnswers.push(row)
    await OfflineStorage.setAnswers(offlineAnswers)
  })
}

// Quita del respaldo la fila del video ya subido y borra su binario de IndexedDB (el try/catch queda en addVideo).
export async function purgeLegacyVideo ({ occurrenceId, itemId, ref }) {
  await withAnswersLock(async () => {
    const offlineAnswers = await OfflineStorage.getAnswers() || []
    const filtered = offlineAnswers.filter(a =>
      !(Number(a.occurrence_id) === Number(occurrenceId) &&
        Number(a.item_id) === Number(itemId) &&
        a.type === 'video')
    )
    await OfflineStorage.setAnswers(filtered)
  })
  await del(ref)
}

const isDocumentOf = (occurrenceId, itemId) => row =>
  String(row.occurrence_id) === String(occurrenceId) &&
  Number(row.item_id) === Number(itemId) &&
  row.type === 'document'

// Reemplaza en el respaldo el documento del ítem por `row` (sin `row`, solo lo quita) en una sola escritura, y
// después libera de IndexedDB los archivos de las filas quitadas.
async function replaceLegacyDocumentRow (occurrenceId, itemId, row = null) {
  const isDocument = isDocumentOf(occurrenceId, itemId)
  const removed = await withAnswersLock(async () => {
    const offlineAnswers = await OfflineStorage.getAnswers()
    const kept = offlineAnswers.filter(answer => !isDocument(answer))
    await OfflineStorage.setAnswers(row ? [...kept, row] : kept)
    return offlineAnswers.filter(isDocument)
  })
  for (const { answer_ref: answerRef } of removed) {
    if (answerRef) await del(answerRef).catch(e => console.error('[DOCUMENT] del blob falló', e))
  }
}

// Guarda en el respaldo el documento de un ítem: el archivo en IndexedDB y una fila con su referencia y nombre,
// que el drenado sube como archivo. Reemplaza el documento que el ítem tuviera pendiente.
export function saveLegacyDocument ({ occurrenceId, itemId, file, filename }) {
  return legacyAddonWriteSerializer(`${occurrenceId}|${itemId}|document`, async () => {
    const answerRef = `document-${Date.now()}-${itemId}`
    await set(answerRef, file)
    try {
      await replaceLegacyDocumentRow(occurrenceId, itemId, {
        occurrence_id: String(occurrenceId),
        form_assignment_location_occurrence_id: String(occurrenceId),
        item_id: itemId,
        type: 'document',
        answer: filename,
        answer_ref: answerRef,
        filename
      })
    } catch (error) {
      await del(answerRef).catch(() => {})
      throw error
    }
  })
}

// Quita del respaldo el documento pendiente del ítem (ya se subió por otro camino).
export function clearLegacyDocument ({ occurrenceId, itemId }) {
  return legacyAddonWriteSerializer(`${occurrenceId}|${itemId}|document`, () => replaceLegacyDocumentRow(occurrenceId, itemId))
}

// Busca en el respaldo la foto nativa sin procesar del ítem (fila sin `type` y con `localPath`). Es solo consulta:
// si el respaldo no se puede leer, se sigue como si no hubiera foto pendiente.
export async function findLegacyRawPhoto (occurrenceId, itemId) {
  let offlineAnswers
  try {
    offlineAnswers = await OfflineStorage.getAnswers() || []
  } catch (error) {
    offlineDbgWarn('photo', 'no se pudo leer el respaldo, se sigue sin foto pendiente', error)
    return undefined
  }

  return offlineAnswers.find(a =>
    String(a.occurrence_id) === String(occurrenceId) &&
    Number(a.item_id) === Number(itemId) &&
    a.localPath &&
    !a.type
  )
}
